/* eslint-disable consistent-return, default-case, no-param-reassign, no-unused-vars */
/*
 *
 * Mortgage reducer
 *
 */

import produce from 'immer';

import Platform from '../../utils/Platform';
import { RESET_SWITCH_LOAN_STATE, RESET_USER_STATE } from '../Main/constants';
import {
  CHANGE_ADD_ESCROW_OPTION,
  CHANGE_STEP,
  CHOOSE_DOCUMENT,
  CHOOSE_DOCUMENT_CANCELLATION,
  CHOOSE_DOCUMENT_FAILURE,
  CHOOSE_DOCUMENT_SUCCESS,
  FETCH_HELOC_DRAW,
  FETCH_HELOC_DRAW_FAILURE,
  FETCH_HELOC_DRAW_SUCCESS,
  FETCH_LOAN,
  FETCH_LOAN_FAILURE,
  FETCH_LOAN_SUCCESS,
  FETCH_MORTGAGE,
  FETCH_MORTGAGE_FAILURE,
  FETCH_MORTGAGE_SUCCESS,
  FETCH_TRANSACTIONS,
  FETCH_TRANSACTIONS_FAILURE,
  FETCH_TRANSACTIONS_SUCCESS,
  FETCH_TRANSACTION_DETAIL,
  FETCH_TRANSACTION_DETAIL_FAILURE,
  FETCH_TRANSACTION_DETAIL_SUCCESS,
  PROCESS_DOCUMENT,
  PROCESS_DOCUMENT_FAILURE,
  PROCESS_DOCUMENT_SUCCESS,
  RESET_ADD_ESCROW_OPTION,
  RESET_BANNER_ERROR,
  RESET_CONFIRMATION,
  RESET_DOCUMENT_STATE,
  RESET_ESCROW_HISTORY_STATE,
  RESET_FORM_ERROR,
  RESET_FORM_ERRORS,
  RESET_FORM_STATE,
  RESET_HELOC_DRAW_FORM_FLOW_STATE,
  RESET_HELOC_DRAW_STATE,
  RESET_REQUEST_STATUS_STATE,
  RESET_STEP_STATE,
  SET_IS_HELOC_DRAW_AVAILABLE,
  SET_SELECTED_MORTGAGE_LOAN,
  SUBMIT_HELOC_DRAW,
  SUBMIT_HELOC_DRAW_FAILURE,
  SUBMIT_HELOC_DRAW_SUCCESS,
  SUBMIT_HELOC_DRAW_VALIDATION_FAILURE,
  UPDATE_FORM_ERROR,
  UPDATE_FORM_VALUE,
  UPDATE_HELOC_DRAW_AMOUNT,
  UPDATE_SELECTED_INSURANCE,
  UPDATE_SELECTED_TRANSACTION,
  UPLOAD_DOCUMENT,
  UPLOAD_DOCUMENT_FAILURE,
  UPLOAD_DOCUMENT_SUCCESS,
  VALIDATE_ROUTING_FAILURE,
  VALIDATE_ROUTING_SUCCESS,
} from './constants';
import { VALIDATE_ROUTING_ERROR_MESSAGE } from '../Payments/Sources/constants';

export const initialState = {
  canUseAnyRoutingNumber: false,
  confirmation: {},
  errors: {
    bannerError: false,
    serviceError: {
      escrow: false,
      helocDraw: false,
      summary: false,
      transactionDetail: false,
      transactions: false,
    },
  },
  escrow: {},
  formErrors: {
    helocDraw: {
      accountNumber: '',
      accountNumberConfirm: '',
      amount: '',
      nameOnAccount: '',
      routingNumber: '',
    },
    uploadDocument: { fileInput: '', secondFileInput: '' },
  },
  forms: {
    helocDraw: {
      accountNumber: '',
      accountNumberConfirm: '',
      accountType: '',
      amount: '',
      nameOnAccount: '',
      routingNumber: '',
    },
    review: { isHelocDrawConfirmChecked: false },
    uploadDocument: {
      fileInput: {
        fileData: '',
        fileName: '',
        fileSize: 0,
        fileType: '',
      },
      secondFileInput: {
        fileData: '',
        fileName: '',
        fileSize: 0,
        fileType: '',
      },
    },
  },
  helocDraw: {
    allowedOperation: null,
    availableAdvanceAmount: null,
    creditLineLimit: null,
    isInHelocDrawFlow: false,
    minimumAdvanceAmount: null,
    routingNumber: '',
    statusBanner: {},
  },
  isAccountRequired: false,
  isHelocDrawAvailable: false,
  itemsPerPage: 20,
  loading: {
    escrow: false,
    summary: true,
    transactionDetail: true,
    transactions: true,
  },
  pageToFetch: 1,
  requestStatus: {
    chooseDocument: 'idle',
    fetchHelocDraw: 'idle',
    processDocument: 'idle',
    submitHelocDraw: 'idle',
    uploadDocument: 'idle',
  },
  selectedInsurance: null,
  selectedMortgageLoan: {},
  selectedTransaction: {},
  selectedTransactionId: '',
  step: 1,
  summary: {},
  totalPages: -1,
  transactions: [],
  validateRoutingError: false,
  validateRoutingSuccess: false,
};

const mortgageReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case CHANGE_ADD_ESCROW_OPTION: {
      const { addEscrowOption } = payload;
      draft.escrow.addEscrowOption = addEscrowOption;
      break;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case CHOOSE_DOCUMENT: {
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        // See comment on Settings/Profile equivalent case for reason on commented out line
        // draft.requestStatus.chooseDocument = 'loading';
        draft.requestStatus.chooseDocument = 'idle';
      } else {
        draft.requestStatus.chooseDocument = 'idle';
      }
      break;
    }
    case CHOOSE_DOCUMENT_CANCELLATION: {
      draft.requestStatus.chooseDocument = 'idle';
      break;
    }
    case CHOOSE_DOCUMENT_FAILURE: {
      const { bannerError } = payload;
      draft.errors.bannerError = bannerError;
      draft.forms.uploadDocument.fileInput = initialState.forms.uploadDocument.fileInput;
      draft.requestStatus.chooseDocument = 'failed';
      break;
    }
    case CHOOSE_DOCUMENT_SUCCESS: {
      draft.requestStatus.chooseDocument = 'succeeded';
      break;
    }
    case FETCH_HELOC_DRAW: {
      draft.errors.serviceError.helocDraw = false;
      draft.requestStatus.fetchHelocDraw = 'loading';
      break;
    }
    case FETCH_HELOC_DRAW_FAILURE: {
      draft.requestStatus.fetchHelocDraw = 'failed';
      break;
    }
    case FETCH_HELOC_DRAW_SUCCESS: {
      const {
        allowedOperation,
        allowedRoutingNumbers,
        availableAdvanceAmount,
        canUseAnyRoutingNumber,
        creditLineLimit,
        isAccountRequired,
        minimumAdvanceAmount,
        statusBanner,
      } = payload;
      draft.canUseAnyRoutingNumber = canUseAnyRoutingNumber;
      draft.isHelocDrawAvailable = true;
      draft.isAccountRequired = isAccountRequired;
      draft.helocDraw.allowedOperation = allowedOperation;
      draft.helocDraw.availableAdvanceAmount = availableAdvanceAmount;
      draft.helocDraw.creditLineLimit = creditLineLimit;
      draft.helocDraw.isInHelocDrawFlow = true;
      draft.helocDraw.minimumAdvanceAmount = minimumAdvanceAmount;
      // TODO For now, we are only supporting one routing number, which is the first returned
      if (isAccountRequired) {
        draft.helocDraw.routingNumber = canUseAnyRoutingNumber ?
          initialState.helocDraw.routingNumber : allowedRoutingNumbers[0].value;
      }
      draft.helocDraw.statusBanner = statusBanner;
      draft.requestStatus.fetchHelocDraw = 'succeded';
      break;
    }
    case FETCH_LOAN: {
      draft.loading.summary = true;
      draft.errors.serviceError.summary = false;
      break;
    }
    case FETCH_LOAN_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.summary = error;
      draft.loading.summary = false;
      break;
    }
    case FETCH_LOAN_SUCCESS: {
      const { summary } = payload;
      draft.summary = summary;
      draft.loading.summary = false;
      break;
    }
    case FETCH_MORTGAGE: {
      draft.loading.escrow = true;
      draft.errors.serviceError.escrow = false;
      break;
    }
    case FETCH_MORTGAGE_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.escrow = error;
      draft.loading.escrow = false;
      break;
    }
    case FETCH_MORTGAGE_SUCCESS: {
      const { mortgageInfo } = payload;
      draft.escrow = mortgageInfo;
      draft.loading.escrow = false;
      break;
    }
    case FETCH_TRANSACTIONS: {
      draft.errors.serviceError.transactions = false;
      draft.loading.transactions = draft.pageToFetch !== -1;
      break;
    }
    case FETCH_TRANSACTIONS_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.transactions = error;
      break;
    }
    case FETCH_TRANSACTIONS_SUCCESS: {
      const { page, totalPages, transactions } = payload;
      draft.errors.serviceError.transactions = false;
      draft.pageToFetch = page === totalPages ? -1 : page + 1;
      draft.loading.transactions = false;
      draft.totalPages = totalPages;
      draft.transactions = draft.transactions.concat(transactions);
      break;
    }
    case FETCH_TRANSACTION_DETAIL: {
      draft.errors.serviceError.transactionDetail = false;
      draft.loading.transactionDetail = true;
      break;
    }
    case FETCH_TRANSACTION_DETAIL_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.transactionDetail = error;
      draft.loading.transactionDetail = false;
      break;
    }
    case FETCH_TRANSACTION_DETAIL_SUCCESS: {
      const { transaction } = payload;
      draft.loading.transactionDetail = false;
      draft.selectedTransaction = transaction;
      break;
    }
    case PROCESS_DOCUMENT: {
      const { file: { name }, isSecondFile } = payload;
      const fileInput = isSecondFile ? 'secondFileInput' : 'fileInput';
      draft.requestStatus.processDocument = 'loading';
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.uploadDocument = initialState.formErrors.uploadDocument;
      draft.forms.uploadDocument[fileInput] = {
        ...initialState.forms.uploadDocument[fileInput],
        fileName: name,
      };
      break;
    }
    case PROCESS_DOCUMENT_FAILURE: {
      const {
        bannerError,
        file,
        isSecondFile,
        validationError,
      } = payload;
      const fileInput = isSecondFile ? 'secondFileInput' : 'fileInput';
      draft.requestStatus.processDocument = 'failed';
      if (bannerError) {
        draft.errors.bannerError = bannerError;
      }
      if (file && validationError) {
        const {
          data: fileData,
          name: fileName,
          size: fileSize,
          type: fileType,
        } = file;
        draft.formErrors.uploadDocument[fileInput] = validationError;
        draft.forms.uploadDocument[fileInput].fileData = fileData;
        draft.forms.uploadDocument[fileInput].fileName = fileName;
        draft.forms.uploadDocument[fileInput].fileSize = fileSize;
        // fileType not available on mobile
        if (fileType) {
          draft.forms.uploadDocument[fileInput].fileType = fileType;
        }
      }
      break;
    }
    case PROCESS_DOCUMENT_SUCCESS: {
      const {
        file: {
          data: fileData,
          name: fileName,
          size: fileSize,
          type: fileType,
        },
        isSecondFile,
      } = payload;
      const fileInput = isSecondFile ? 'secondFileInput' : 'fileInput';
      draft.requestStatus.processDocument = 'succeeded';
      draft.forms.uploadDocument[fileInput].fileData = fileData;
      draft.forms.uploadDocument[fileInput].fileName = fileName;
      draft.forms.uploadDocument[fileInput].fileSize = fileSize;
      // fileType not available on mobile
      if (fileType) {
        draft.forms.uploadDocument[fileInput].fileType = fileType;
      }
      break;
    }
    case RESET_ADD_ESCROW_OPTION: {
      if ('addEscrowOption' in draft.escrow) delete draft.escrow.addEscrowOption;
      break;
    }
    case RESET_BANNER_ERROR: {
      draft.errors.bannerError = initialState.errors.bannerError;
      break;
    }
    case RESET_CONFIRMATION: {
      draft.confirmation = initialState.confirmation;
      break;
    }
    case RESET_DOCUMENT_STATE: {
      const { doc } = payload;
      draft.errors = initialState.errors;
      draft.formErrors.uploadDocument[doc] = initialState.formErrors.uploadDocument[doc];
      draft.forms.uploadDocument[doc] = initialState.forms.uploadDocument[doc];
      break;
    }
    case RESET_ESCROW_HISTORY_STATE: {
      draft.errors.serviceError.transactions = initialState.errors.serviceError.transactions;
      draft.loading.transactions = initialState.loading.transactions;
      draft.pageToFetch = initialState.pageToFetch;
      draft.totalPages = initialState.totalPages;
      draft.transactions = initialState.transactions;
      break;
    }
    case RESET_FORM_ERROR: {
      const { field, form } = payload;
      draft.formErrors[form][field] = initialState.formErrors[form][field];
      break;
    }
    case RESET_FORM_ERRORS: {
      const { form } = payload;
      draft.formErrors[form] = initialState.formErrors[form];
      break;
    }
    case RESET_FORM_STATE: {
      const { form } = payload;
      draft.errors = initialState.errors;
      draft.formErrors[form] = initialState.formErrors[form];
      draft.forms[form] = initialState.forms[form];
      draft.validateRoutingError = initialState.validateRoutingError;
      draft.validateRoutingSuccess = initialState.validateRoutingSuccess;
      break;
    }
    case RESET_HELOC_DRAW_FORM_FLOW_STATE: {
      draft.formErrors.helocDraw = initialState.formErrors.helocDraw;
      draft.forms.helocDraw = initialState.forms.helocDraw;
      draft.step = initialState.step;
      draft.validateRoutingError = initialState.validateRoutingError;
      draft.validateRoutingSuccess = initialState.validateRoutingSuccess;
      break;
    }
    case RESET_HELOC_DRAW_STATE: {
      draft.errors.serviceError.helocDraw = initialState.errors.serviceError.helocDraw;
      draft.formErrors.helocDraw = initialState.formErrors.helocDraw;
      draft.forms.helocDraw = initialState.forms.helocDraw;
      draft.helocDraw = initialState.helocDraw;
      draft.confirmation = initialState.confirmation;
      draft.requestStatus.fetchHelocDraw = initialState.requestStatus.fetchHelocDraw;
      draft.requestStatus.submitHelocDraw = initialState.requestStatus.submitHelocDraw;
      draft.step = initialState.step;
      draft.validateRoutingError = initialState.validateRoutingError;
      draft.validateRoutingSuccess = initialState.validateRoutingSuccess;
      break;
    }
    case RESET_REQUEST_STATUS_STATE: {
      const { state } = payload;
      draft.requestStatus[state] = initialState.requestStatus[state];
      break;
    }
    case RESET_STEP_STATE: {
      draft.step = initialState.step;
      break;
    }
    case RESET_SWITCH_LOAN_STATE: {
      return initialState;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case SET_IS_HELOC_DRAW_AVAILABLE: {
      draft.isHelocDrawAvailable = false;
      draft.requestStatus.fetchHelocDraw = 'succeded';
      break;
    }
    case SET_SELECTED_MORTGAGE_LOAN: {
      const { loan } = payload;
      draft.selectedMortgageLoan = loan;
      break;
    }
    case SUBMIT_HELOC_DRAW: {
      draft.requestStatus.submitHelocDraw = 'loading';
      break;
    }
    case SUBMIT_HELOC_DRAW_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.helocDraw = error;
      draft.requestStatus.submitHelocDraw = 'failed';
      break;
    }
    case SUBMIT_HELOC_DRAW_SUCCESS: {
      const {
        confirmationNumber,
        requestAmount,
        requestDate,
      } = payload;
      draft.confirmation.confirmationNumber = confirmationNumber;
      draft.confirmation.requestAmount = requestAmount;
      draft.confirmation.requestDate = requestDate;
      draft.helocDraw = initialState.helocDraw;
      draft.requestStatus.submitHelocDraw = 'succeeded';
      break;
    }
    case SUBMIT_HELOC_DRAW_VALIDATION_FAILURE: {
      const { field, message } = payload;
      draft.formErrors.helocDraw[field] = message;
      draft.step = 1;
      draft.requestStatus.submitHelocDraw = 'idle';
      break;
    }
    case UPDATE_FORM_ERROR: {
      const { field, form, value } = payload;
      draft.formErrors[form][field] = value;
      break;
    }
    case UPDATE_FORM_VALUE: {
      const { field, form, value } = payload;
      draft.forms[form][field] = value;
      break;
    }
    case UPDATE_HELOC_DRAW_AMOUNT: {
      const { amount } = payload;
      if (draft.formErrors.helocDraw.amount) {
        draft.formErrors.helocDraw.amount = initialState.formErrors.helocDraw.amount;
      }
      draft.forms.helocDraw.amount = amount;
      break;
    }
    case UPDATE_SELECTED_INSURANCE: {
      const { selectedType } = payload;
      draft.selectedInsurance =
        draft.escrow.insuranceData.find(({ insuranceType }) => selectedType === insuranceType);
      break;
    }
    case UPDATE_SELECTED_TRANSACTION: {
      const { id } = payload;
      draft.selectedTransactionId = id;
      if (!id) {
        draft.selectedTransaction = {};
        draft.errors.serviceError.transactionDetail = false;
      }
      break;
    }
    case UPLOAD_DOCUMENT: {
      draft.requestStatus.uploadDocument = 'loading';
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.uploadDocument = initialState.formErrors.uploadDocument;
      break;
    }
    case UPLOAD_DOCUMENT_FAILURE: {
      const { bannerError, isSecondFile, validationError } = payload;
      const fileInput = isSecondFile ? 'secondFileInput' : 'fileInput';
      const hasPartialError = !!bannerError || !!validationError;
      draft.requestStatus.uploadDocument = 'failed';
      if (hasPartialError) {
        if (bannerError) {
          draft.errors.bannerError = bannerError;
        }
        if (validationError) {
          draft.formErrors.uploadDocument[fileInput] = validationError;
        }
      } else {
        // Desktop full error navigation handled here. Mobile is handled in saga.
        draft.step += 2;
      }
      break;
    }
    case UPLOAD_DOCUMENT_SUCCESS: {
      draft.requestStatus.uploadDocument = 'succeeded';
      draft.step += 1;
      break;
    }
    case VALIDATE_ROUTING_FAILURE: {
      draft.validateRoutingError = VALIDATE_ROUTING_ERROR_MESSAGE;
      break;
    }
    case VALIDATE_ROUTING_SUCCESS: {
      const { isValid, routingNumber } = payload;
      if (isValid) {
        draft.validateRoutingSuccess = true;
        draft.helocDraw.routingNumber = routingNumber;
        draft.validateRoutingError = initialState.validateRoutingError;
      } else {
        draft.validateRoutingSuccess = false;
        draft.helocDraw.routingNumber = initialState.helocDraw.routingNumber;
      }
      break;
    }
  }
}, initialState);

export default mortgageReducer;
